import { disableDebugTools } from '@angular/platform-browser';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { disableBrowserDevTools } from './app/disable-devtools';
import { loadGoogleMaps } from './app/load-google-maps';

// Global handler for chunk load errors that bypass Angular's ErrorHandler
// These occur when lazy-loaded modules fail after a deployment
const CHUNK_RELOAD_KEY = 'elite:chunk-error-reload';
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (error instanceof Error && /loading chunk|ChunkLoadError|dynamically imported module/i.test(error.message)) {
    if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      console.warn('[main.ts] Stale chunk detected — reloading to fetch latest version.');
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
    }
  }
});

if (environment && environment.appName) {
  try {
    document.title = environment.appName;
  } catch {
    // document might be unavailable in some contexts; ignore gracefully
  }
}

if (environment.production) {
  disableBrowserDevTools();
}

/**
 * Google Maps is fetched before boot so the dispatch map can construct `<google-map>` synchronously,
 * but the application is *not* held hostage to it (#632). Whatever the outcome — loaded, failed, or
 * still in flight past the timeout — Angular bootstraps. Only the dispatch map degrades; sign-in,
 * tickets, drivers, settings and reports never needed Maps in the first place.
 *
 * `libraries` asks for exactly what the code uses, which until #665 it did not:
 *
 * - `marker` — `<map-advanced-marker>` renders a `google.maps.marker.AdvancedMarkerElement`, on the
 *   report ride map and the arrived-and-waiting dialog. The screen this note used to cite,
 *   `queue-map.component.ts`, went with the `queue-area` domain in #643; the requirement did not go
 *   with it. It was originally being used without requesting the library, working only because
 *   something else pulled it in — a latent bug, not a licence.
 * - `drawing` was requested and never used — the polygon drawing in `ui-draw-area-form` is
 *   hand-rolled — so it is gone.
 * - `geometry` was requested for two functions, `encoding.decodePath` and
 *   `spherical.computeDistanceBetween`, both now local pure helpers in `@elite/shared/util`. Pure
 *   arithmetic does not need a CDN, and five of the call sites ran outside any template, where a
 *   view gate could not protect them.
 */
const GOOGLE_MAPS_SRC = `https://maps.googleapis.com/maps/api/js?libraries=marker&key=${environment.googleMapsApiKey}`;

loadGoogleMaps(GOOGLE_MAPS_SRC).then((result) => {
  if (result !== 'loaded') {
    console.warn(
      `[main.ts] Google Maps failed to load (${result}) — booting without it. Map screens will show a fallback.`,
    );
  }

  return bootstrapApplication(AppComponent, appConfig)
    .then((appRef) => {
      if (environment.production) {
        disableDebugTools();
      }
      return appRef;
    })
    .catch((err) => console.error(err));
});
